import type { ToolCallBlock, TurnBlock } from './types.js';

/**
 * Collapses the harness's "read the file before you write it" detour.
 *
 * Claude Code refuses a `Write`/`Edit` on a file the session has not read — or
 * has read but not since it last changed on disk — so a transcript regularly
 * carries three rows for one edit: a write that failed on that rule, the read
 * it was told to do, then the same write again.
 * The failed row says nothing about the agent's work — it is the harness
 * talking to itself — so the transcript drops it and marks the write that
 * followed instead, keeping the fact that a retry happened (and the message
 * that caused it) on the row a reader actually cares about.
 *
 * Only the exact shape is collapsed: the failed write, a *successful* read of
 * the same path, and another write of that path, with no other tool call in
 * between. Anything else (a read that failed, a detour through a different
 * file, an unrelated call spliced in) is real history and stays as it is. The
 * final write may itself fail; that failure is the agent's, not the harness's.
 */

/** Marker key on the retried write block's `extra`. */
export const READ_BEFORE_WRITE_RETRY = 'readBeforeWriteRetry';

export interface ReadBeforeWriteRetry {
  /** File both write attempts targeted, as the agent spelled it. */
  path: string;
  /** The refusal the erased attempt came back with. */
  message: string;
}

/**
 * Tools that put bytes on disk and are gated behind the read-first rule. Named
 * rather than derived from `kind: 'edit'` because only these carry a
 * `file_path` this module can pair a read against; a delete or a move is a
 * different operation with a different rule.
 */
const WRITE_TOOLS = new Set(['write', 'edit', 'multiedit', 'notebookedit']);
const READ_TOOLS = new Set(['read', 'notebookread']);

/**
 * The refusal text, across the phrasings Claude Code has shipped. Matching the
 * message (not just "a failed write followed by a read") is what keeps a write
 * that failed for a real reason — a permission error, a bad path — visible.
 *
 * The rule has two halves and both produce the same three-row shape: the file
 * was never read, or it was read and something (a formatter, a linter, the
 * user) changed it since. Every pattern here is a string Claude Code actually
 * ships — a guessed phrasing would read as coverage this module does not have.
 */
const REFUSAL_PATTERNS = [
  // Never read in this session.
  /file has not been read yet/i,
  /file must be read first/i,
  // Read, but stale. Two wordings: the validation path and the mtime check a
  // Bash-run formatter trips.
  /file has been modified since read/i,
  /file content has changed since it was last read/i,
];

function toolName(block: ToolCallBlock): string {
  const raw = typeof block.extra?.toolName === 'string' ? block.extra.toolName : block.title;
  return raw.trim().toLowerCase();
}

/**
 * The file a call names. `notebook_path` covers the notebook tools, which spell
 * the same argument differently.
 */
function filePath(block: ToolCallBlock): string | null {
  const input = block.rawInput;
  if (!input) return null;
  for (const key of ['file_path', 'notebook_path']) {
    const value = input[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return null;
}

function outputText(block: ToolCallBlock): string {
  const fromContent = block.content
    .filter((item) => item.type === 'content' && item.text)
    .map((item) => item.text!)
    .join('\n');
  if (fromContent) return fromContent;
  // A provider can report the failure only on the raw payload, with no content
  // item to show. Stringifying it is enough to match against and is what the
  // failed row itself falls back to rendering.
  return block.rawOutput ? JSON.stringify(block.rawOutput) : '';
}

/** The refusal message when a failed write was rejected by the read-first rule. */
function refusalMessage(block: ToolCallBlock): string | null {
  if (block.status !== 'failed' || !WRITE_TOOLS.has(toolName(block))) return null;
  const text = outputText(block);
  return REFUSAL_PATTERNS.some((pattern) => pattern.test(text)) ? text : null;
}

/** The retry marker a block carries, or null. */
export function readBeforeWriteRetryOf(block: TurnBlock): ReadBeforeWriteRetry | null {
  if (block.type !== 'tool_call') return null;
  const marker = block.extra?.[READ_BEFORE_WRITE_RETRY];
  if (!marker || typeof marker !== 'object') return null;
  const { path, message } = marker as Record<string, unknown>;
  if (typeof path !== 'string' || typeof message !== 'string') return null;
  return { path, message };
}

/**
 * Drops each read-first refusal and marks the write that followed it. Returns
 * the input array unchanged when nothing matches, so a turn that never hit the
 * rule costs one pass and no new identities downstream.
 */
export function collapseReadBeforeWriteRetries(blocks: TurnBlock[]): TurnBlock[] {
  const toolCalls: number[] = [];
  for (let i = 0; i < blocks.length; i++) {
    if (blocks[i].type === 'tool_call') toolCalls.push(i);
  }
  if (toolCalls.length < 3) return blocks;

  const erased = new Set<number>();
  const marked = new Map<number, ReadBeforeWriteRetry>();

  // Text and thinking blocks between the three calls are expected — the agent
  // narrates the detour — so consecutiveness is measured over tool calls.
  for (let t = 0; t + 2 < toolCalls.length; t++) {
    const failed = blocks[toolCalls[t]] as ToolCallBlock;
    const message = refusalMessage(failed);
    if (!message) continue;
    const path = filePath(failed);
    if (!path) continue;

    const read = blocks[toolCalls[t + 1]] as ToolCallBlock;
    if (read.status !== 'completed' || !READ_TOOLS.has(toolName(read))) continue;
    if (filePath(read) !== path) continue;

    const retry = blocks[toolCalls[t + 2]] as ToolCallBlock;
    if (!WRITE_TOOLS.has(toolName(retry)) || filePath(retry) !== path) continue;

    erased.add(toolCalls[t]);
    marked.set(toolCalls[t + 2], { path, message });
    // The retry closes this triple. Resuming from it lets a second refusal on
    // the same file collapse in turn, without matching one triple twice.
    t += 1;
  }

  if (erased.size === 0) return blocks;

  const result: TurnBlock[] = [];
  for (let i = 0; i < blocks.length; i++) {
    if (erased.has(i)) continue;
    const marker = marked.get(i);
    if (!marker) {
      result.push(blocks[i]);
      continue;
    }
    // Only tool-call indices ever land in `marked`.
    const retry = blocks[i] as ToolCallBlock;
    result.push({ ...retry, extra: { ...retry.extra, [READ_BEFORE_WRITE_RETRY]: marker } });
  }
  return result;
}
